import { Rng } from '../../core/Rng';

/**
 * Seeded coherent noise for terrain and texture generation (SPEC §3.9.1, §5.2).
 *
 * 2-D simplex noise with a seeded permutation table, plus the three composites the theatre needs:
 * fractal Brownian motion for the general shape, ridged multifractal for the mountain spine, and
 * domain warping to break up the characteristic "noise look" that otherwise reads as a computer
 * generated a landscape rather than as geology.
 *
 * Everything here is a pure function of (seed, position), so the same seed produces the same
 * theatre on every machine — which is what lets the heightfield be generated at build time and
 * committed rather than shipped as a black box.
 */

const F2 = 0.5 * (Math.sqrt(3) - 1);
const G2 = (3 - Math.sqrt(3)) / 6;

/** Gradient directions for 2-D simplex; 12 evenly spread vectors. */
const GRAD_X = new Float32Array([1, -1, 1, -1, 1, -1, 1, -1, 0, 0, 0, 0]);
const GRAD_Y = new Float32Array([1, 1, -1, -1, 0, 0, 0, 0, 1, -1, 1, -1]);

export class SimplexNoise {
  private readonly perm = new Uint8Array(512);
  private readonly permMod12 = new Uint8Array(512);

  constructor(seed: number) {
    const rng = new Rng(seed);
    const source = new Uint8Array(256);
    for (let i = 0; i < 256; i += 1) source[i] = i;

    // Fisher–Yates with the seeded generator: a shuffle, not a hash, so the distribution is even.
    for (let i = 255; i > 0; i -= 1) {
      const j = rng.int(0, i);
      const swap = source[i];
      source[i] = source[j];
      source[j] = swap;
    }

    for (let i = 0; i < 512; i += 1) {
      this.perm[i] = source[i & 255];
      this.permMod12[i] = this.perm[i] % 12;
    }
  }

  /** 2-D simplex noise in roughly [-1, 1]. */
  noise2D(x: number, y: number): number {
    const s = (x + y) * F2;
    const i = Math.floor(x + s);
    const j = Math.floor(y + s);
    const t = (i + j) * G2;

    const x0 = x - (i - t);
    const y0 = y - (j - t);

    const i1 = x0 > y0 ? 1 : 0;
    const j1 = x0 > y0 ? 0 : 1;

    const x1 = x0 - i1 + G2;
    const y1 = y0 - j1 + G2;
    const x2 = x0 - 1 + 2 * G2;
    const y2 = y0 - 1 + 2 * G2;

    const ii = i & 255;
    const jj = j & 255;

    let total = 0;

    let t0 = 0.5 - x0 * x0 - y0 * y0;
    if (t0 > 0) {
      const g = this.permMod12[ii + this.perm[jj]];
      t0 *= t0;
      total += t0 * t0 * (GRAD_X[g] * x0 + GRAD_Y[g] * y0);
    }

    let t1 = 0.5 - x1 * x1 - y1 * y1;
    if (t1 > 0) {
      const g = this.permMod12[ii + i1 + this.perm[jj + j1]];
      t1 *= t1;
      total += t1 * t1 * (GRAD_X[g] * x1 + GRAD_Y[g] * y1);
    }

    let t2 = 0.5 - x2 * x2 - y2 * y2;
    if (t2 > 0) {
      const g = this.permMod12[ii + 1 + this.perm[jj + 1]];
      t2 *= t2;
      total += t2 * t2 * (GRAD_X[g] * x2 + GRAD_Y[g] * y2);
    }

    return 70 * total;
  }

  /** Fractal Brownian motion: octaves of noise at halving amplitude, doubling frequency. */
  fbm(x: number, y: number, octaves: number, lacunarity = 2, gain = 0.5): number {
    let amplitude = 1;
    let frequency = 1;
    let sum = 0;
    let normalisation = 0;
    for (let octave = 0; octave < octaves; octave += 1) {
      sum += amplitude * this.noise2D(x * frequency, y * frequency);
      normalisation += amplitude;
      amplitude *= gain;
      frequency *= lacunarity;
    }
    return sum / normalisation;
  }

  /**
   * Ridged multifractal, in [0, 1]. Folding the noise about zero and inverting it turns smooth
   * hills into sharp crests — this is what makes the mountain spine read as a mountain spine
   * rather than as large dunes.
   */
  ridged(x: number, y: number, octaves: number, lacunarity = 2.03, gain = 0.5): number {
    let amplitude = 1;
    let frequency = 1;
    let sum = 0;
    let normalisation = 0;
    let previous = 1;
    for (let octave = 0; octave < octaves; octave += 1) {
      const signal = 1 - Math.abs(this.noise2D(x * frequency, y * frequency));
      const sharpened = signal * signal * previous;
      previous = sharpened;
      sum += amplitude * sharpened;
      normalisation += amplitude;
      amplitude *= gain;
      frequency *= lacunarity;
    }
    return sum / normalisation;
  }

  /**
   * Domain-warped fBm: displace the sample point by another noise field before sampling.
   * Cheap, and it is the single most effective trick for making procedural terrain stop looking
   * procedural — drainage patterns and ridge lines start to bend the way real ones do.
   */
  warpedFbm(x: number, y: number, octaves: number, warpStrength: number): number {
    const warpX = this.fbm(x + 5.2, y + 1.3, 3);
    const warpY = this.fbm(x + 9.7, y + 4.1, 3);
    return this.fbm(x + warpStrength * warpX, y + warpStrength * warpY, octaves);
  }
}

/** Smoothstep between two edges. */
export function smoothstep(edge0: number, edge1: number, value: number): number {
  if (edge0 === edge1) return value < edge0 ? 0 : 1;
  const t = clamp01((value - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
}

export function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Shortest distance from a point to a segment, in the same units as the inputs. */
export function distanceToSegment(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  const abx = bx - ax;
  const aby = by - ay;
  const lengthSquared = abx * abx + aby * aby;
  if (lengthSquared < 1e-9) return Math.hypot(px - ax, py - ay);
  let t = ((px - ax) * abx + (py - ay) * aby) / lengthSquared;
  t = clamp01(t);
  return Math.hypot(px - (ax + abx * t), py - (ay + aby * t));
}

/** Shortest distance from a point to a polyline. */
export function distanceToPolyline(
  px: number,
  py: number,
  points: readonly (readonly [number, number])[],
): number {
  let best = Number.POSITIVE_INFINITY;
  for (let i = 0; i < points.length - 1; i += 1) {
    const a = points[i];
    const b = points[i + 1];
    const distance = distanceToSegment(px, py, a[0], a[1], b[0], b[1]);
    if (distance < best) best = distance;
  }
  return best;
}
